import parseQueryStringCaseInsensitive from "@insite/client-framework/Common/Utilities/parseQueryStringCaseInsensitive";
import { getBillTos } from "@insite/client-framework/Services/CustomersService";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillTosDataView, getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { BillToModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";

import Button from "@insite/mobius/Button/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";

import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";

import { ValidatorRuleType } from "@tKgpco/Common/Utilities/validate";
import MultiSelectDropdown, { Option } from "@tKgpco/Components/MultiSelectDropdown";
import { useForm } from "@tKgpco/Custom/Hooks/useForm";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import {
    FeatureRolePermissionsProperty,
    getOOTBPageDetailsHn,
    getOOTBWidgetDetailsHn,
    getPageDetailsHn,
    getPagesWidgetHn,
    PageDetail,
    WidgetDetail,
} from "@tKgpco/Services/SecurityRolesService";

import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";

import updateRole from "@tKgpco/Store/Data/SecurityRoles/Handlers/AddRole";
import loadRoleDetail from "@tKgpco/Store/Data/SecurityRoles/Handlers/LoadRoleDetail";

import { AddGetBillTosParameter } from "@tKgpco/Widgets/SecurityRoles/AddSecurityRoles";

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import siteMessage from "@insite/client-framework/SiteMessage";

const mapStateToProps = (state: CustomApplicationState) => {
    const { getBillTosParameter } = state.pages.rfqMyQuotes;

    const location = getLocation(state);

    let roleName = "";

    if (location?.search) {
        const parsedQuery = parseQueryStringCaseInsensitive(location.search, {
            roleName: "",
        });

        roleName = parsedQuery.roleName;
    }

    return {
        billTosDataView:
            getBillTosParameter &&
            getBillTosDataView(state, {
                ...getBillTosParameter,
                allCustomers: true,
                filter: "",
            } as AddGetBillTosParameter),

        contextBillTo: state.context.session.billToId,
        settings: getSettingsCollection(state),
        getBillTosParameter,

        billToState: getBillToState(state, state.context.session.billToId),

        roleDetailState: state.KGPState.SecurityRolesStore.rolesDetail,

        roleName,
    };
};

const mapDispatchToProps = {
    updateRole,
    loadRoleDetail,
};

interface CustomWidgetStyles {
    mainGridContainer?: GridContainerProps;
    heading?: TypographyPresentationProps;
}

const styles: CustomWidgetStyles = {
    mainGridContainer: {
        offsetCss: css`
            max-width: 700px;
            margin: 0 auto;
            background: #f1f6fb;
            border: 1px solid #e1e7ed;
            border-bottom-left-radius: 8px;
            border-bottom-right-radius: 8px;

            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            max-width: 100%;
                        `,
                        css``,
                        css``,
                        css``,
                    ],
                    "max",
                );
            }}
        `,
        css: css`
            .dropdown {
                [class^="StyledButton"] {
                    background-color: #fff;
                }
            }
        `,
    },

    heading: {
        css: css`
            text-transform: uppercase;
            font-size: 22px;
            color: #0b70b7;
            border-bottom: 1px solid #0b70b7;
            padding-bottom: 5px;
            width: 100%;
        `,
    },
};

const EMPTY_STRING = "";

interface AccessorialsFormValues {
    name: string;
}

const formValues: AccessorialsFormValues = {
    name: EMPTY_STRING,
};

const initialErrorState = {
    name: EMPTY_STRING,
};

const validatorConfig: {
    [key in keyof AccessorialsFormValues]: ValidatorRuleType;
} = {
    name: {
        required: true,
        label: "Name",
        requiredMsg: "Name is required",
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const buildDropdownOptions = (items: { id: string; name: string }[]): Option[] => {
    const uniqueOptions = new Map<string, string[]>();

    items.forEach(item => {
        const existing = uniqueOptions.get(item.name) || [];

        existing.push(item.id);

        uniqueOptions.set(item.name, existing);
    });

    return Array.from(uniqueOptions, ([name, values]) => ({
        value: values.join(","),
        label: name,
    }))
        .filter(option => {
            return !(option.label.length === 1 && (option.label.includes("'") || option.label.includes("`")));
        })
        .sort((a, b) => a.label.localeCompare(b.label));
};

const EditSecurityRoles: React.FC<Props> = ({
    history,
    updateRole,
    loadRoleDetail,
    roleName,
    roleDetailState,
    getBillTosParameter,
}) => {
    const toasterContext = useContext(ToasterContext);

    const { fieldChangeHandler, updateFormState, updateFormErrors, formState } = useForm({
        formValues,
        initialErrorState,
        validatorConfig,
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    const [pageDetails, setPageDetails] = useState<PageDetail[]>([]);
    const [ootbPageDetails, setOOTBPageDetails] = useState<PageDetail[]>([]);
    const [widgetDetails, setWidgetDetails] = useState<WidgetDetail[]>([]);
    const [billToCollection, setBillToCollection] = useState<BillToModel[]>([]);
    const [ootbWidgetDetails, setOOTBWidgetDetails] = useState<WidgetDetail[]>([]);

    const [selectedPages, setSelectedPages] = useState<Option[]>([]);
    const [selectedOOTBPages, setSelectedOOTBPages] = useState<Option[]>([]);
    const [selectedWidgets, setSelectedWidgets] = useState<Option[]>([]);
    const [selectedOOTBWidgets, setSelectedOOTBWidgets] = useState<Option[]>([]);
    const [selectedCustomers, setSelectedCustomers] = useState<Option[]>([]);
    const [hasFeatureChanges, setHasFeatureChanges] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const initializedRef = useRef(false);

    // ---------------------------------------
    // Load page details once
    // ---------------------------------------

    useEffect(() => {
        getPageDetailsHn().then(response => {
            setPageDetails(response.customPageDetails ?? []);
        });

        getOOTBPageDetailsHn().then(response => {
            setOOTBPageDetails(response.oobPageDetails ?? []);
        });
    }, []);

    // ---------------------------------------
    // Load customers once
    // ---------------------------------------

    useEffect(() => {
        setIsLoading(true);
        getBillTos({
            ...getBillTosParameter,
            allCustomers: true,
            pageSize: 2500,
        } as AddGetBillTosParameter).then(response => {
            setBillToCollection(response.billTos ?? []);
            setIsLoading(false);
        });
    }, [getBillTosParameter]);

    // ---------------------------------------
    // Load role detail
    // ---------------------------------------

    useEffect(() => {
        initializedRef.current = false;

        if (!roleName) {
            return;
        }

        loadRoleDetail({ roleName });
    }, [roleName]);

    // ---------------------------------------
    // Memoized dropdown options
    // ---------------------------------------

    const pageOptions = useMemo(() => {
        return buildDropdownOptions(
            pageDetails.map(page => ({
                id: page.id,
                name: page.featureName,
            })),
        );
    }, [pageDetails]);

    const widgetOptions = useMemo(() => {
        return buildDropdownOptions(
            widgetDetails.map(widget => ({
                id: widget.id,
                name: widget.featureSubPage,
            })),
        );
    }, [widgetDetails]);

    const ootbPageOptions = useMemo(() => {
        return buildDropdownOptions(
            ootbPageDetails.map(page => ({
                id: page.id,
                name: page.featureName,
            })),
        );
    }, [ootbPageDetails]);

    const ootbWidgetOptions = useMemo(() => {
        return buildDropdownOptions(
            ootbWidgetDetails.map(widget => ({
                id: widget.id,
                name: widget.featureSubPage,
            })),
        );
    }, [ootbWidgetDetails]);

    const customerOptions = useMemo(() => {
        return billToCollection
            .map(customer => ({
                value: customer.customerNumber,
                label: customer.customerNumber,
            }))
            .filter((option, index, self) => index === self.findIndex(x => x.value === option.value))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [billToCollection]);

    // ---------------------------------------
    // Hydrate form once
    // ---------------------------------------

    // ---------------------------------------
    // Load widgets when pages change
    // ---------------------------------------

    useEffect(() => {
        if (!selectedPages.length) {
            setWidgetDetails([]);
            setSelectedWidgets([]);
            return;
        }

        const payload = selectedPages.map(page => page.label).join(",");

        getPagesWidgetHn({
            pageName: payload,
        }).then(response => {
            const widgets = response.customWidgetDetails ?? [];

            setWidgetDetails(widgets);

            // Keep only widgets that still belong
            // to selected pages
            setSelectedWidgets(prev =>
                prev.filter(selected => widgets.some(widget => widget.featureSubPage === selected.label)),
            );
        });
    }, [selectedPages]);

    useEffect(() => {
        if (!selectedOOTBPages.length) {
            setOOTBWidgetDetails([]);
            setSelectedOOTBWidgets([]);
            return;
        }

        const payload = selectedOOTBPages.map(page => page.label).join(",");

        getOOTBWidgetDetailsHn({
            pageName: payload,
        }).then(response => {
            const widgets = response.customWidgetDetails ?? [];

            setOOTBWidgetDetails(widgets);

            // Keep only widgets that still belong
            // to selected pages
            setSelectedOOTBWidgets(prev =>
                prev.filter(selected => widgets.some(widget => widget.featureSubPage === selected.label)),
            );
        });
    }, [selectedOOTBPages]);

    useEffect(() => {
        if (!roleDetailState || !pageOptions.length || !customerOptions.length || initializedRef.current) {
            return;
        }

        const { roleName, customerNumber, featureDetails } = roleDetailState;

        // -----------------------------
        // Role name
        // -----------------------------

        updateFormState({
            name: roleName || "",
        });

        // -----------------------------
        // Customers
        // -----------------------------

        const customerNumbers =
            customerNumber
                ?.split(",")
                .map(x => x.trim())
                .filter(Boolean) ?? [];

        const customerSelections = customerOptions.filter(option => customerNumbers.includes(option.label));

        setSelectedCustomers(customerSelections);

        // -----------------------------
        // Pages
        // -----------------------------

        const pageNames =
            featureDetails?.flatMap(detail => detail.featureName?.split(",").map(x => x.trim()) || []) ?? [];

        const uniquePageNames = [...new Set(pageNames)];

        const pageSelections = pageOptions.filter(page => uniquePageNames.includes(page.label));

        setSelectedPages(pageSelections);

        const ootbPageNames =
            featureDetails
                ?.filter(detail => detail.isOotbFeature)
                .flatMap(detail => detail.featureName?.split(",").map(x => x.trim()) || []) ?? [];

        const uniqueOOTBPageNames = [...new Set(ootbPageNames)];

        const ootbPageSelections = ootbPageOptions.filter(page => uniqueOOTBPageNames.includes(page.label));

        setSelectedOOTBPages(ootbPageSelections);

        // -----------------------------
        // Load widgets for selected pages
        // -----------------------------

        const pagePayload = uniquePageNames.join(",");

        getPagesWidgetHn({
            pageName: pagePayload,
        }).then(response => {
            const widgets = response.customWidgetDetails ?? [];

            setWidgetDetails(widgets);

            // -----------------------------
            // Widget selections
            // -----------------------------

            const widgetNames =
                featureDetails?.flatMap(detail => detail.featureSubPage?.split(",").map(x => x.trim()) || []) ?? [];

            const uniqueWidgetNames = [...new Set(widgetNames)];

            const widgetSelections = widgets
                .map(widget => ({
                    value: widget.id,
                    label: widget.featureSubPage,
                }))
                .filter(widget => uniqueWidgetNames.includes(widget.label));

            setSelectedWidgets(widgetSelections);

            initializedRef.current = true;
        });

        const ootbPagePayload = uniqueOOTBPageNames.join(",");

        getOOTBWidgetDetailsHn({
            pageName: ootbPagePayload,
        }).then(response => {
            const widgets = response.oobWidgetDetails ?? [];

            setOOTBWidgetDetails(widgets);

            // -----------------------------
            // Widget selections
            // -----------------------------

            const ootbWidgetNames =
                featureDetails
                    ?.filter(detail => detail.isOotbFeature)
                    .flatMap(detail => detail.featureSubPage?.split(",").map(x => x.trim()) || []) ?? [];

            const uniqueOOTBWidgetNames = [...new Set(ootbWidgetNames)];

            const ootbWidgetSelections = widgets
                .map(widget => ({
                    value: widget.id,
                    label: widget.featureSubPage,
                }))
                .filter(widget => uniqueOOTBWidgetNames.includes(widget.label));

            setSelectedOOTBWidgets(ootbWidgetSelections);

            initializedRef.current = true;
        });
    }, [roleDetailState, pageOptions, customerOptions]);

    // ---------------------------------------
    // Generic toggle helper
    // ---------------------------------------

    const toggleSelection = useCallback(
        (option: Option, selected: Option[], setter: React.Dispatch<React.SetStateAction<Option[]>>) => {
            const exists = selected.some(item => item.value === option.value);

            if (exists) {
                setter(selected.filter(item => item.value !== option.value));
            } else {
                setter([...selected, option]);
            }
        },
        [],
    );

    // ---------------------------------------
    // Handlers
    // ---------------------------------------

    const handlePageChange = useCallback(
        (option: Option) => {
            setHasFeatureChanges(true);

            toggleSelection(option, selectedPages, setSelectedPages);
        },
        [selectedPages, toggleSelection],
    );

    const handleWidgetChange = useCallback(
        (option: Option) => {
            setHasFeatureChanges(true);

            toggleSelection(option, selectedWidgets, setSelectedWidgets);
        },
        [selectedWidgets, toggleSelection],
    );

    const handleOOTBPageChange = useCallback(
        (option: Option) => {
            setHasFeatureChanges(true);

            toggleSelection(option, selectedOOTBPages, setSelectedOOTBPages);
        },
        [selectedOOTBPages, toggleSelection],
    );

    const handleOOTBWidgetChange = useCallback(
        (option: Option) => {
            setHasFeatureChanges(true);

            toggleSelection(option, selectedOOTBWidgets, setSelectedOOTBWidgets);
        },
        [selectedOOTBWidgets, toggleSelection],
    );

    const handleCustomerChange = useCallback(
        (option: Option) => {
            toggleSelection(option, selectedCustomers, setSelectedCustomers);
        },
        [selectedCustomers, toggleSelection],
    );

    const handleSearchChange = useCallback(
        (searchText: string) => {
            getBillTos({
                ...getBillTosParameter,
                allCustomers: true,
                filter: searchText,
            } as AddGetBillTosParameter).then(response => {
                setBillToCollection(response.billTos ?? []);
            });
        },
        [getBillTosParameter],
    );

    // ---------------------------------------
    // Validation
    // ---------------------------------------

    const isNotValid = useMemo(() => {
        return formState.name.trim() === "" || !selectedCustomers.length;
    }, [formState.name, selectedCustomers]);

    // ---------------------------------------
    // Payload
    // ---------------------------------------

    const rolePayload: FeatureRolePermissionsProperty = useMemo(() => {
        const validPages = [...selectedPages, ...selectedOOTBPages].filter(page => page.label?.trim());

        const validWidgets = [...selectedWidgets, ...selectedOOTBWidgets].filter(widget => widget.label?.trim());

        const hasPages = validPages.length > 0;

        const hasWidgets = validWidgets.length > 0;

        const hasFeatures = hasPages || hasWidgets;

        const isEditMode = !!roleName && !!roleDetailState;

        const payload: FeatureRolePermissionsProperty = {
            isEdit: isEditMode,

            roleName: formState.name,

            customerNumber: selectedCustomers.map(customer => customer.label).join(","),

            // IMPORTANT:
            // default empty array
            featureDetails: [],
        };

        // Only populate when actual data exists
        if (hasFeatures) {
            payload.featureDetails = [
                {
                    featureName: validPages.map(page => page.label).join(","),

                    featureSubPage: validWidgets.map(widget => widget.label).join(","),
                },
            ];
        }

        return payload;
    }, [
        formState.name,
        selectedCustomers,
        selectedPages,
        selectedWidgets,
        roleDetailState,
        roleName,
        selectedOOTBPages,
        selectedOOTBWidgets,
    ]);

    // ---------------------------------------
    // Update handler
    // ---------------------------------------

    const updateRoleHandler = useCallback(() => {
        setIsSubmitting(true);

        updateRole({
            ...rolePayload,

            onSuccess: (result: any) => {
                setIsSubmitting(false);

                updateFormState(formValues);

                updateFormErrors(initialErrorState);

                history.push("/MyAccount/SecurityRoles");

                toasterContext.addToast({
                    messageType: "success",
                    body: translate(`${result.message}.`),
                    timeoutLength: 4000,
                });
            },
        });
    }, [updateRole, rolePayload, updateFormState, updateFormErrors, history, toasterContext]);

    return (
        <LoadingOverlay loading={isLoading}>
            <GridContainer {...styles.mainGridContainer}>
                <GridItem width={12}>
                    <Typography as="h2" {...styles.heading}>
                        {translate("Update Security Role")}
                    </Typography>
                </GridItem>

                <GridItem width={12}>
                    <TextField
                        label={translate("Name")}
                        name="name"
                        value={formState.name}
                        required
                        disabled
                        onChange={e =>
                            fieldChangeHandler({
                                name: e.currentTarget.name,
                                value: e.currentTarget.value,
                            })
                        }
                    />
                </GridItem>

                <GridItem width={12} className="dropdown">
                    <MultiSelectDropdown
                        label={translate("Customer *")}
                        handleOptionClick={handleCustomerChange}
                        options={customerOptions}
                        selectedOptions={selectedCustomers}
                        placeHolder={translate("Select Customers")}
                        isSearchEnabled
                        handleSearchChange={handleSearchChange}
                        searchPlaceHolder={translate("Search Customers")}
                    />
                </GridItem>

                <GridItem width={12} className="dropdown">
                    <MultiSelectDropdown
                        label={translate("Show Pages")}
                        handleOptionClick={handlePageChange}
                        options={pageOptions}
                        selectedOptions={selectedPages}
                        placeHolder={translate("Select Pages")}
                        tooltipText={siteMessage("SecurityRoles_ShowPagesTooltip") as string}
                    />
                </GridItem>

                <GridItem width={12} className="dropdown">
                    <MultiSelectDropdown
                        label={translate("Show Widgets")}
                        handleOptionClick={handleWidgetChange}
                        options={widgetOptions}
                        selectedOptions={selectedWidgets}
                        placeHolder={translate("Select Widgets")}
                        isDisabled={!selectedPages.length}
                    />
                </GridItem>

                <GridItem width={12} className="dropdown">
                    <MultiSelectDropdown
                        label={translate("Hide Pages")}
                        handleOptionClick={handleOOTBPageChange}
                        options={ootbPageOptions}
                        selectedOptions={selectedOOTBPages}
                        placeHolder={translate("Select OOTB Pages")}
                        tooltipText={siteMessage("SecurityRoles_HideOOTBPagesTooltip") as string}
                    />
                </GridItem>

                <GridItem width={12} className="dropdown">
                    <MultiSelectDropdown
                        label={translate("Hide Widgets")}
                        handleOptionClick={handleOOTBWidgetChange}
                        options={ootbWidgetOptions}
                        selectedOptions={selectedOOTBWidgets}
                        placeHolder={translate("Select OOTB Widgets")}
                        isDisabled={!selectedOOTBPages.length}
                    />
                </GridItem>

                <GridItem>
                    <Button onClick={updateRoleHandler} disabled={isNotValid || isSubmitting}>
                        Update
                    </Button>
                </GridItem>
            </GridContainer>
        </LoadingOverlay>
    );
};

const editSecurityRolesWidget: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(EditSecurityRoles)),

    definition: {
        group: "Basic",
    },
};

export default editSecurityRolesWidget;
